import { getOrBuildSchema, isComputedFlag } from './schema-cache'
import { getEntityRegistry } from '@/Atomic/Widgets/AiChatBot/core/engine/entity-registry'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import type {
  EntityClassName,
  SessionContext,
} from '@/Atomic/Widgets/AiChatBot/core/engine/types'

export type ScrivitoAccessor = { id: () => string; get: (f: string) => unknown }

export interface SerializedItem {
  _id: string
  _dataClass: string
  _url?: string
  [field: string]: unknown
}

/**
 * Delegating re-export: routes through the host adapter so tools/shared has no
 * direct Scrivito DataClass imports. The Scrivito impl lives in
 * host/scrivito/scrivitoDataAccessAdapter.ts.
 *
 * Returns an opaque handle (`unknown`): callers use it as an existence guard
 * (`if (!resolveDataClass(x))`) or pass it straight to the adapter's
 * queryResolved/getResolved/countResolved, which accept it opaquely and cast it
 * back to a DataClass internally — so no core code touches Scrivito types.
 */
export function resolveDataClass(dataClass: string): unknown {
  return getHostRuntime().data.resolveDataClass(dataClass)
}

export function unknownEntityError(dataClass: string) {
  return { error: `Unknown entity type: ${dataClass}` }
}

export function requireDataClassName(raw: string): EntityClassName | null {
  // A registry key is a concrete browsable entity; the polymorphic reference base
  // (CRM's Contact) is a valid search target that is intentionally NOT in the
  // registry. Lazy: the registry is injected at bootstrap, so read it at call time.
  if (raw in getEntityRegistry()) {
    return raw
  }
  if (getHostRuntime().data.isPolymorphicReferenceBase(raw)) {
    return raw
  }
  return null
}

export async function serializeDataItems(
  items: unknown[],
  dataClass: string,
  ctx?: SessionContext,
): Promise<SerializedItem[]> {
  // Use the session-context schema cache when available (avoids a redundant
  // getSchemaAttributes fetch on cache hits). Falls back to a direct fetch when
  // no ctx is provided or when the schema has not been cached yet.
  let fieldNames: string[] | undefined

  const dcName = requireDataClassName(dataClass)
  if (dcName && ctx) {
    const schema = await getOrBuildSchema(dcName, ctx)
    if (schema) {
      // Serialize the full attribute set minus computed flags, not
      // schema.attributes (visible-only): ungrouped system fields like
      // createdAt/number are dropped from the visible list but the prompt still
      // renders them. Readonly booleans are excluded (see serializableAttributeNames).
      fieldNames = schema.serializableAttributeNames
    }
  }

  if (!fieldNames) {
    const { data } = getHostRuntime()
    const { schemaAttributes } = await data.getSchema(
      data.getSchemaName(dataClass),
    )
    if (schemaAttributes) {
      fieldNames = Object.keys(schemaAttributes).filter((name) => {
        const attr = schemaAttributes[name]
        return !attr || !isComputedFlag(attr)
      })
    }
  }

  // Resolve all URLs in parallel via the navigation connector
  const nav = getHostRuntime().navigation
  const urls = await Promise.all(
    items.map((item) => nav.urlFor(item).catch(() => undefined)),
  )

  return items.map((item, i) => {
    const typedItem = item as unknown as ScrivitoAccessor
    const row: SerializedItem = {
      _id: typedItem.id(),
      _dataClass: dataClass,
      _url: urls[i],
    }
    if (fieldNames) {
      for (const field of fieldNames) {
        row[field] = typedItem.get(field)
      }
    }
    // displayTitle is a Scrivito virtual field — not in schema but always available
    row['displayTitle'] = typedItem.get('displayTitle')
    return row
  })
}
